import axios from 'axios';

// Get API URL from env or fallback to Render URL for production
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // If in production, use the Render URL
  if (process.env.NODE_ENV === 'production') {
    return 'https://oc-ccs-student-information-system.onrender.com/api';
  }
  // Otherwise, use local network for mobile testing
};

const API_URL = getApiUrl();

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect for login/verification endpoints - let them handle their own errors
      const url = error.config?.url || '';
      const isAuthEndpoint = url.includes('/auth/login') || 
                             url.includes('/auth/student-login') || 
                             url.includes('/auth/student-verify') ||
                             url.includes('/auth/resend-code');
      
      if (!isAuthEndpoint) {
        // Clear token and redirect to login for other 401 errors
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  studentLogin: (credentials) => api.post('/auth/student-login', credentials),
  studentVerify: (credentials) => api.post('/auth/student-verify', credentials),
  resendCode: (credentials) => api.post('/auth/resend-code', credentials),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
};

// Students API
export const studentsAPI = {
  getAll: (params) => api.get('/students', { params }),
  getById: (id) => api.get(`/students/${id}`),
  getByYear: (yearLevel, params) => api.get(`/students/year/${yearLevel}`, { params }),
  getStats: () => api.get('/students/stats'),
};

// Chat API
export const chatAPI = {
  sendMessage: (message) => api.post('/chat', { message }),
  sendFileMessage: (file, message) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('message', message || 'Analyze this file');
    return api.post('/chat/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000, // 120s timeout for file processing + AI analysis
    });
  },
  getSuggestions: () => api.get('/chat/suggestions'),
  getHistory: () => api.get('/chat/history'),
  saveHistory: (messages) => api.post('/chat/history', { messages }),
  clearHistory: () => api.delete('/chat/history')
};

// Admin API
export const adminAPI = {
  // Dashboard
  getDashboard: () => api.get('/admin/dashboard'),

  // Students CRUD
  getStudents: (params) => api.get('/admin/students', { params }),
  getStudent: (id) => api.get(`/admin/students/${id}`),
  createStudent: (data) => api.post('/admin/students', data),
  updateStudent: (id, data) => api.put(`/admin/students/${id}`, data),
  deleteStudent: (id) => api.delete(`/admin/students/${id}`),
  forceLogoutStudent: (id) => api.post(`/admin/students/${id}/force-logout`),
  restoreStudent: (id) => api.post(`/admin/students/${id}/restore`),

  // Teachers CRUD
  getTeachers: (params) => api.get('/admin/teachers', { params }),
  createTeacher: (data) => api.post('/admin/teachers', data),
  updateTeacher: (id, data) => api.put(`/admin/teachers/${id}`, data),
  deleteTeacher: (id) => api.delete(`/admin/teachers/${id}`),
  resetTeacherPassword: (id, password) => api.post(`/admin/teachers/${id}/reset-password`, { password }),
  forceLogoutTeacher: (id) => api.post(`/admin/teachers/${id}/force-logout`),

  // Logs
  getLogs: (params) => api.get('/admin/logs', { params }),
  deleteLog: (id) => api.delete(`/admin/logs/${id}`),
  deleteAllLogs: () => api.delete('/admin/logs'),
  deleteSelectedLogs: (ids) => api.post('/admin/logs/delete-selected', { ids }),
};

// Upload API
export const uploadAPI = {
  uploadProfileImage: (file) => {
    const formData = new FormData();
    formData.append('profileImage', file);
    return api.post('/upload/profile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  uploadStudentProfileImage: (file) => {
    const formData = new FormData();
    formData.append('profileImage', file);
    return api.post('/upload/student-profile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  uploadTeacherProfileImage: (file) => {
    const formData = new FormData();
    formData.append('profileImage', file);
    return api.post('/upload/teacher-profile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  deleteProfileImage: (filename) => api.delete(`/upload/profile/${filename}`),
};

// Grades API
export const gradesAPI = {
  // Grades
  getGrades: (params) => api.get('/grades', { params }),
  getStudentGrades: (studentId, semester, schoolYear, yearLevel) =>
    api.get(`/grades/student/${studentId}`, { params: { semester, schoolYear, yearLevel } }),
  createGrade: (data) => api.post('/grades', data),
  createBulkGrades: (grades) => api.post('/grades/bulk', { grades }),
  updateGrade: (id, data) => api.put(`/grades/${id}`, data),
  deleteGrade: (id) => api.delete(`/grades/${id}`),
  getGWA: (studentId, semester, schoolYear) =>
    api.get(`/grades/gwa/${studentId}`, { params: { semester, schoolYear } }),

  // Academic History (4-period grading)
  getAcademicHistory: (studentId) => api.get(`/grades/academic-history/${studentId}`),
  getGradesByYear: (studentId, yearLevel, semester, schoolYear) =>
    api.get(`/grades/year/${yearLevel}/student/${studentId}`, { params: { semester, schoolYear } }),
  getGWAWithPeriods: (studentId, yearLevel, semester, schoolYear) =>
    api.get(`/grades/gwa-periods/${studentId}`, { params: { yearLevel, semester, schoolYear } }),

  // Subjects
  getSubjects: (semester) => api.get('/grades/subjects', { params: { semester } }),
  createSubject: (data) => api.post('/grades/subjects', data),
  updateSubject: (id, data) => api.put(`/grades/subjects/${id}`, data),
  deleteSubject: (id) => api.delete(`/grades/subjects/${id}`),

  // School Years
  getSchoolYears: () => api.get('/grades/school-years'),
};

// Enrollments API
export const enrollmentsAPI = {
  // Get all enrollments with filters
  getEnrollments: (params) => api.get('/enrollments', { params }),
  
  // Get enrollments for a specific student
  getStudentEnrollments: (studentId, params) => 
    api.get(`/enrollments/student/${studentId}`, { params }),
  
  // Get enrollments for a section (class list)
  getSectionEnrollments: (params) => 
    api.get('/enrollments/section', { params }),
  
  // Check if student is enrolled in a subject
  checkEnrollment: (params) => 
    api.get('/enrollments/check', { params }),
  
  // Create single enrollment
  createEnrollment: (data) => api.post('/enrollments', data),
  
  // Bulk enroll students
  bulkEnroll: (enrollments) => api.post('/enrollments/bulk', { enrollments }),
  
  // Update enrollment (change section)
  updateEnrollment: (id, data) => api.put(`/enrollments/${id}`, data),
  
  // Delete single enrollment
  deleteEnrollment: (id) => api.delete(`/enrollments/${id}`),
  
  // Delete all enrollments for a student in a semester
  deleteStudentEnrollments: (studentId, params) => 
    api.delete(`/enrollments/student/${studentId}`, { params }),
};

export default api;
